import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { ProductListElementDataType } from '@/components/sections/builder/elements/body/product-grid/type';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import CollectionSetting from '@/components/sections/builder/settings/collection-setting';
import { Input } from '@/components/ui/input';

import { Separator } from '@/components/ui/separator';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Switch } from '@/components/ui/switch';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ProductListElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Product Source</h1>
      <CollectionSetting
        value={data.source}
        onChange={(val) =>
          builder.setElementData(element.id, {
            source: val,
          })
        }
      />
      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Heading Settings</h1>
      <div className='mb-2 mt-4 '>Heading label</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setElementData(element.id, {
            headingLabel: value,
          })
        }
        value={data.headingLabel}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Heading alignment</h4>
        <InputSelect
          defaultValue={data.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Heading size</h4>
        <InputSelect
          defaultValue={data.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              headingSize: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Description Settings</h1>
      <div className='mb-2 mt-4 '>Description</div>
      <div className='relative w-full'>
        <TextEditorMinimal
          onChange={(e) => {
            builder.setElementData(element.id, {
              textLabel: e,
            });
          }}
          value={data.textLabel}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Show Collection Description</label>
        <Switch
          className='ml-auto'
          checked={data.showAdminCollectionDesc}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showAdminCollectionDesc: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Description style</h4>
        <InputSelect
          defaultValue={data.textStyle}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              textStyle: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Product card settings</h1>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Limit show product</h4>
        <InputSlider
          value={data.limit || 0}
          max={25}
          onChange={(val) =>
            builder.setElementData(element.id, {
              limit: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Image ratio</h4>
        <InputSelect
          defaultValue={data.imageRatio}
          options={[
            { label: 'Adapt to image', value: 'adaptToImage' },
            { label: 'Portrait', value: 'portrait' },
            { label: 'Square', value: 'square' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              imageRatio: v,
            })
          }
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Show second image on hover</label>
        <Switch
          className='ml-auto'
          checked={data.showSecondImageOnHover}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on desktop</h4>
        <InputSlider
          value={data.totalCol || 1}
          min={1}
          max={5}
          onChange={(val) =>
            builder.setElementData(element.id, {
              totalCol: val,
            })
          }
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on mobile</h4>
        <InputSlider
          value={data.totalColMobile || 1}
          min={1}
          max={3}
          onChange={(val) =>
            builder.setElementData(element.id, {
              totalColMobile: val,
            })
          }
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Make products full width</label>
        <Switch
          className='ml-auto'
          checked={data.fullWidth}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              fullWidth: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Enable quick add button</label>
        <Switch
          className='ml-auto'
          checked={data.enableQuickAddButton}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableQuickAddButton: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Show image</label>
        <Switch
          className='ml-auto'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showImage: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Show shadows</label>
        <Switch
          className='ml-auto'
          checked={data.showShadow}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showShadow: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-4 flex items-center gap-2'>
        <label>Show category</label>
        <Switch
          className='ml-auto'
          checked={data.showCategory}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showCategory: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 mt-4 font-bold'>&quot;View all&quot; Style</div>
      <div className='my-4 flex items-center gap-2'>
        <label> Enable &quot;View all&quot; if collection has more products than shown</label>
        <Switch
          className='ml-auto'
          checked={data.showViewAll}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showViewAll: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label view all</label>
        <Input
          className='w-full p-2 text-xs'
          name='labelViewAll'
          defaultValue={data.labelViewAll}
          value={data.labelViewAll}
          maxLength={30}
          onChange={(e) => {
            builder.setElementData(element.id, {
              labelViewAll: e.target.value,
            });
          }}
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Position view all</h4>
        <InputSelect
          defaultValue={data.positionViewAll}
          options={[
            { label: 'Top', value: 'top' },
            { label: 'Bottom', value: 'bottom' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              positionViewAll: v,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''> View all style</h4>
        <InputSelect
          defaultValue={data.viewAllStyle}
          options={[
            { label: 'Link', value: 'link' },
            { label: 'Outline button', value: 'outline' },
            { label: 'Solid button', value: 'default' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              viewAllStyle: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Style Settings</h1>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <div className='mb-2 mt-4 font-bold'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-4 mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <BackgroundSetting
        value={data.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      {data?.background?.type !== 'image' && (
        <>
          <div className='mb-4 mt-4 font-bold'>Overlay</div>
          <ColorPicker
            color={data.overlay}
            inputColor={data.overlay}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) =>
              builder.setElementData(element.id, { overlay: e.target.value })
            }
            onChangePercentage={(val) =>
              builder.setElementData(element.id, { overlayPercentage: val })
            }
          />
        </>
      )}
    </div>
  );
}
